// Draws the Market Flow board into one SVG (1190 tall; 1490 wide, or wider to fill a wide screen).
// Imperative D3, called from React; behaviour and look match the original vanilla board.
import * as d3 from 'd3';
import { pctOf, type Analysed, type Market, type SectorStat, type Snapshot } from '@planb/core';
import { palette } from './palette';

type Sel = d3.Selection<any, any, any, any>;

export interface BoardUi {
  t: () => number;
  isShortlisted: (symbol: string) => boolean;
  selectSector: (name: string | null) => void;
  highlight: (symbol: string, on: boolean) => void;
  tooltip: (ev: MouseEvent | null, s?: Analysed, rank?: number) => void;
  openStock: (symbol: string, ev?: MouseEvent | KeyboardEvent) => void;
}

export interface BoardView {
  /** Full sector list, kept while a sector filter narrows `snap`. */
  sectors?: SectorStat[];
  sector?: string | null;
  /** Design-space width (≥ 1490). Wider boards give the extra room to the flow plot and the cards. */
  width?: number;
}

const BASE_W = 1490, H = 1190;
// The theme's colours, refreshed at the start of each render (see charts/palette.ts).
let C = palette();
const BASE_COLS = [{ n: 8, w: 104 }, { n: 5, w: 124 }, { n: 4, w: 146 }, { n: 3, w: 190 }];

/** Horizontal layout for a board `width` wide; vertical positions never change. */
function layout(width: number) {
  const extra = Math.max(0, width - BASE_W);
  const plotX1 = 650 + extra * 0.4;
  const colX1 = plotX1 + 110 + extra * 0.04;
  const cardX = colX1 + 128;
  // Cards fill to 14px from the right edge; three 8px gaps between the four columns.
  const k = (width - 14 - cardX - 24) / BASE_COLS.reduce((x, c) => x + c.w, 0);
  return {
    W: width,
    L: { plotX0: 75, plotX1, colX1, yTop: 113, yBot: 1135, cardX, bullY0: 22, bullY1: 578, bearY0: 602, bearY1: 1160 },
    COLS: BASE_COLS.map((c) => ({ n: c.n, w: c.w * k })),
  };
}
// Set at the start of each renderBoard() call (rendering is synchronous).
let { W, L, COLS } = layout(BASE_W);
const FONT_NUM = "var(--font-mono), ui-monospace, monospace";

export const fmtPct = (p: number) => (p >= 0 ? '+' : '') + p.toFixed(2) + '%';
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
/** Latest sweep in the last hour that price hasn't closed back through. */
export const liveSweep = (s: Analysed, t: number) => s.sweeps.filter((w) => !w.failed && w.idx >= t - 12).pop() || null;

interface YScale { (p: number): number; ticks: { v: number; y: number }[] }

// Adaptive y-scale: each side stretches where stocks are crowded so the dense ±1% band gets
// most of the room, like a quantile ruler.
function buildYScale(values: number[], y0: number): YScale {
  const POS = [0.2, 0.5, 1, 2, 3, 5, 8, 12, 20];
  const NEG = [0.2, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 5, 8, 12, 20];

  function side(mags: number[], cands: number[], yStart: number, yEnd: number) {
    const v = mags.slice().sort((a, b) => a - b);
    const max = v.length ? v[v.length - 1] : 0.5;
    const ticks = [0];
    for (const c of cands) { ticks.push(c); if (c >= max) break; }
    const n = ticks.length, top = ticks[n - 1];
    const ecdf = (x: number) => {
      if (!v.length) return x / top;
      let lo = 0, hi = v.length;
      while (lo < hi) { const m = (lo + hi) >> 1; if (v[m] <= x) lo = m + 1; else hi = m; }
      return lo / v.length;
    };
    const ys = ticks.map((tk, i) => yStart + (yEnd - yStart) * (i === n - 1 ? 1 : 0.3 * (i / (n - 1)) + 0.7 * ecdf(tk)));
    const map = (m: number) => {
      if (m <= 0) return yStart;
      if (m >= top) return yEnd;
      let i = 1;
      while (ticks[i] < m) i++;
      return ys[i - 1] + ((ys[i] - ys[i - 1]) * (m - ticks[i - 1])) / (ticks[i] - ticks[i - 1]);
    };
    return { ticks, ys, map };
  }

  const pos = side(values.filter((v) => v > 0), POS, y0, L.yTop);
  const neg = side(values.filter((v) => v <= 0).map((v) => -v), NEG, y0, L.yBot);
  const y = ((p: number) => (p >= 0 ? pos.map(p) : neg.map(-p))) as YScale;
  y.ticks = [
    ...pos.ticks.map((v, i) => ({ v, y: pos.ys[i] })),
    ...neg.ticks.slice(1).map((v, i) => ({ v: -v, y: neg.ys[i + 1] })),
  ];
  return y;
}

function defs(svg: Sel) {
  const d = svg.append('defs');
  const grad = (id: string, color: string, top: number, bottom: number) => {
    const g = d.append('linearGradient').attr('id', id).attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', 1);
    g.append('stop').attr('offset', 0).attr('stop-color', color).attr('stop-opacity', top);
    g.append('stop').attr('offset', 1).attr('stop-color', color).attr('stop-opacity', bottom);
  };
  grad('hazeUp', C.g, 0.22, 0.03);
  grad('hazeDn', C.r, 0.03, 0.22);
  grad('cardBull', C.g, 0.02, 0.16);
  grad('cardBear', C.r, 0.02, 0.16);
  for (const [id, color] of [['hatchG', C.gd], ['hatchR', C.rd]]) {
    const p = d.append('pattern').attr('id', id).attr('width', 4).attr('height', 4)
      .attr('patternUnits', 'userSpaceOnUse').attr('patternTransform', 'rotate(45)');
    p.append('line').attr('x1', 0).attr('y1', 0).attr('x2', 0).attr('y2', 4)
      .attr('stroke', color).attr('stroke-width', 1).attr('stroke-opacity', 0.45);
  }
}

function badge(g: Sel, n: number, x: number, y: number) {
  const b = g.append('g').attr('class', 'explain-badge').attr('transform', `translate(${x},${y})`);
  b.append('circle').attr('r', 10).attr('fill', C.blue);
  b.append('text').text(n).attr('text-anchor', 'middle').attr('dy', 4).attr('fill', '#fff')
    .attr('font-size', 11).attr('font-weight', 700).attr('font-family', FONT_NUM);
}

/** Time-axis labels: the open, every full hour, and the close. */
function timeMarks(market: Market): [number, string][] {
  const out: [number, string][] = [[0, market.timeLabel(0)]];
  for (let i = 1; i < market.bars; i++) if ((market.openMinute + i * market.barMinutes) % 60 === 0) out.push([i, market.timeLabel(i)]);
  out.push([market.bars, market.timeLabel(market.bars)]);
  return out;
}

export function renderBoard(svgEl: SVGSVGElement, market: Market, snap: Snapshot, ui: BoardUi, view: BoardView = {}) {
  C = palette();
  const timeX = (i: number) => L.plotX0 + ((L.plotX1 - L.plotX0) * i) / market.bars;
  const fmtPx = market.formatPrice;
  const sectors = view.sectors || snap.sectors;
  ({ W, L, COLS } = layout(Math.max(BASE_W, Math.round(view.width ?? BASE_W))));
  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.attr('viewBox', `0 0 ${W} ${H}`);
  defs(svg);

  const total = snap.stocks.length || 1;
  const y0 = clamp(L.yTop + ((L.yBot - L.yTop) * snap.bulls.length) / total, L.yTop + 90, L.yBot - 90);
  const y = buildYScale(snap.stocks.map((s) => s.pct), y0);
  const top = new Set([...snap.topBulls, ...snap.topBears].map((s) => s.symbol));

  // ── 1. Breadth strip ──────────────────────────────────────────────
  {
    const g = svg.append('g');
    const sx0 = 45, sx1 = L.plotX1 + 25, sxm = (sx0 + sx1) / 2;
    const sx = d3.scaleLinear([-4, 4], [sx0, sx1]).clamp(true);
    const ly = 68;
    g.append('line').attr('x1', sx0).attr('x2', sxm).attr('y1', ly).attr('y2', ly).attr('stroke', C.rs).attr('stroke-width', 1.5);
    g.append('line').attr('x1', sxm).attr('x2', sx1).attr('y1', ly).attr('y2', ly).attr('stroke', C.gs).attr('stroke-width', 1.5);
    g.append('text').text('-4%').attr('x', sx0 - 7).attr('y', ly + 3).attr('text-anchor', 'end').attr('class', 'axis-label');
    g.append('text').text('+4%').attr('x', sx1 + 7).attr('y', ly + 3).attr('class', 'axis-label');

    const stacks = new Map<string, number>();
    snap.stocks.slice().sort((a, b) => Math.abs(a.pct) - Math.abs(b.pct)).forEach((s) => {
      const bx = Math.round(sx(s.pct) / 5);
      const key = bx + (s.bull ? 'u' : 'd');
      const k = stacks.get(key) || 0;
      stacks.set(key, k + 1);
      if (k > 8) return;
      const cy = s.bull ? ly - 6 - k * 5 : ly + 6 + k * 5;
      g.append('rect').attr('x', bx * 5 - 2).attr('y', cy - 2).attr('width', 4).attr('height', 4)
        .attr('fill', s.bull ? C.g : C.r).attr('opacity', 0.75);
    });

    sectors.forEach((sec) => {
      const cx = sx(sec.avg), col = sec.avg > 0 ? C.gd : C.rd;
      g.append('circle').attr('cx', cx).attr('cy', ly).attr('r', 5.5).attr('fill', C.panel).attr('stroke', col).attr('stroke-width', 1.4);
      g.append('circle').attr('cx', cx).attr('cy', ly).attr('r', 2).attr('fill', col);
    });

    if (sectors.length) {
      const lead = sectors[0], lag = sectors[sectors.length - 1];
      const label = (x: number, title: string, sec: SectorStat, arrow: 'up' | 'down') => {
        const t = g.append('text').attr('x', x).attr('y', 22).attr('class', 'eyebrow');
        if (arrow === 'up') t.append('tspan').text('↗ ').attr('fill', C.g);
        t.append('tspan').text(title + ' ');
        t.append('tspan').text(sec.name + ' ').attr('class', 'eyebrow-strong');
        t.append('tspan').text(fmtPct(sec.avg)).attr('fill', sec.avg > 0 ? C.g : C.r);
        if (arrow === 'down') t.append('tspan').text(' ↘').attr('fill', C.r);
        g.append('path').attr('d', `M${x + 60},28 L${x + 60},36 L${sx(sec.avg)},${ly - 8}`)
          .attr('fill', 'none').attr('stroke', C.mute).attr('stroke-width', 0.7).attr('stroke-dasharray', '2 2');
      };
      label(sx0 + (sx1 - sx0) * 0.095, 'LAGGING SECTOR', lag, 'down');
      label(sx0 + (sx1 - sx0) * 0.627, 'LEADING SECTOR', lead, 'up');
    }

    g.append('text').text('▲ BULLISH').attr('x', L.colX1 + 5).attr('y', 22).attr('text-anchor', 'end').attr('class', 'eyebrow');
    g.append('text').text(snap.bulls.length).attr('x', L.colX1 + 5).attr('y', 48).attr('text-anchor', 'end').attr('class', 'counter').attr('fill', C.g);
    g.append('text').text('▼ BEARISH').attr('x', L.colX1 + 5).attr('y', 63).attr('text-anchor', 'end').attr('class', 'eyebrow');
    g.append('text').text(snap.bears.length).attr('x', L.colX1 + 5).attr('y', 89).attr('text-anchor', 'end').attr('class', 'counter').attr('fill', C.r);
    badge(g, 1, 20, 22);
  }

  // ── 2. Y axis ─────────────────────────────────────────────────────
  {
    const g = svg.append('g');
    g.append('rect').attr('x', 24).attr('y', L.yTop).attr('width', 4).attr('height', y0 - L.yTop).attr('fill', C.g).attr('opacity', 0.85);
    g.append('rect').attr('x', 24).attr('y', y0).attr('width', 4).attr('height', L.yBot - y0).attr('fill', C.r).attr('opacity', 0.85);
    g.append('rect').attr('x', 20).attr('y', y0 - 1.5).attr('width', 12).attr('height', 3).attr('fill', C.ink);
    let lastY = -Infinity;
    y.ticks.slice().sort((a, b) => a.y - b.y).forEach((tk) => {
      g.append('line').attr('x1', L.plotX0).attr('x2', L.plotX1).attr('y1', tk.y).attr('y2', tk.y)
        .attr('stroke', C.grid).attr('stroke-dasharray', '1 3');
      if (tk.y - lastY < 11) return;
      lastY = tk.y;
      g.append('text').text(tk.v === 0 ? '0' : (tk.v > 0 ? '+' : '') + tk.v).attr('x', 64).attr('y', tk.y + 3)
        .attr('text-anchor', 'end').attr('class', 'axis-label');
      g.append('line').attr('x1', 66).attr('x2', 71).attr('y1', tk.y).attr('y2', tk.y).attr('stroke', C.mute);
    });
    timeMarks(market).forEach(([i, lbl]) => {
      g.append('text').text(lbl).attr('x', timeX(i)).attr('y', L.yBot + 22).attr('text-anchor', 'middle').attr('class', 'axis-label');
    });
  }

  // ── 3. Flow lines, haze, order blocks, sweeps ─────────────────────
  {
    const g = svg.append('g');
    const T = snap.t;
    const up: number[] = [], dn: number[] = [];
    for (let i = 0; i <= T; i++) {
      const vals = snap.stocks.map((s) => s.series[i]);
      const p = vals.filter((v) => v > 0).sort(d3.ascending);
      const n = vals.filter((v) => v <= 0).sort(d3.ascending);
      up.push(p.length ? d3.quantile(p, 0.8)! : 0);
      dn.push(n.length ? d3.quantile(n, 0.2)! : 0);
    }
    const area = d3.area<number>().x((_d, i) => timeX(i)).y0(y0).y1((d) => y(d)).curve(d3.curveMonotoneX);
    g.append('path').attr('d', area(up)).attr('fill', 'url(#hazeUp)');
    g.append('path').attr('d', area(dn)).attr('fill', 'url(#hazeDn)');

    const line = d3.line<number>().x((_d, i) => timeX(i)).y((d) => y(d)).curve(d3.curveMonotoneX);
    const ordered = snap.stocks.slice().sort((a, b) => Number(top.has(a.symbol)) - Number(top.has(b.symbol)));
    ordered.forEach((s) => {
      const hot = top.has(s.symbol);
      g.append('path').attr('class', 'stock-line').attr('data-sym', s.symbol)
        .attr('d', line(s.series)).attr('fill', 'none')
        .attr('stroke', s.bull ? C.g : C.r).attr('stroke-width', hot ? 0.9 : 0.55)
        .attr('opacity', hot ? 0.55 : 0.16);
    });

    [...snap.topBulls, ...snap.topBears].forEach((s) => {
      s.obs.slice(-3).forEach((ob) => { // the latest three; older ones only add clutter
        const col = ob.type === 'bull' ? C.gd : C.rd;
        const ya = y(pctOf(ob.top, s.prevClose)), yb = y(pctOf(ob.bottom, s.prevClose));
        const x1 = timeX(ob.idx + 1), x2 = timeX(ob.mitigated ? (ob.endIdx ?? T) + 1 : T);
        // Thin bands: the stretched scale would otherwise blow small ranges up into big boxes.
        const hh = clamp(Math.abs(yb - ya), 3, 7), yy = (ya + yb) / 2 - hh / 2;
        g.append('rect').attr('class', 'ob-zone').attr('data-sym', s.symbol)
          .attr('x', x1).attr('y', yy).attr('width', Math.max(2, x2 - x1)).attr('height', hh)
          .attr('fill', col).attr('fill-opacity', ob.mitigated ? 0.03 : 0.08)
          .attr('stroke', col).attr('stroke-opacity', ob.mitigated ? 0.25 : 0.5)
          .attr('stroke-width', 0.6).attr('stroke-dasharray', '2 2');
        g.append('rect').attr('x', x1 - 2.5).attr('y', yy + hh / 2 - 2.5).attr('width', 5).attr('height', 5)
          .attr('fill', C.panel).attr('stroke', col).attr('stroke-width', 0.8).attr('opacity', 0.8);
      });
      s.sweeps.filter((w) => !w.failed).forEach((w) => {
        const sx = timeX(w.idx + 1), sy = y(pctOf(w.level, s.prevClose)), dir = w.type === 'bear' ? 1 : -1;
        g.append('path').attr('class', 'sweep-mark').attr('data-sym', s.symbol)
          .attr('d', `M${sx - 3.5},${sy - 6 * dir} L${sx + 3.5},${sy - 6 * dir} L${sx},${sy - 1 * dir} Z`)
          .attr('fill', C.amber).attr('opacity', 0.85)
          .append('title').text(`${s.symbol}: swept ${w.name} at ${market.timeLabel(w.idx)}`);
      });
    });

    if (T < market.bars) {
      const nx = timeX(T);
      g.append('line').attr('x1', nx).attr('x2', nx).attr('y1', L.yTop).attr('y2', L.yBot)
        .attr('stroke', C.blue).attr('stroke-dasharray', '3 3').attr('opacity', 0.7);
      g.append('text').text(snap.time).attr('x', nx).attr('y', L.yTop - 6).attr('text-anchor', 'middle').attr('class', 'now-label');
    }
    badge(g, 3, timeX(market.bars * 0.4), L.yTop + 40);
    badge(g, 4, timeX(market.bars * 0.78), y0 - 40);
  }

  // ── 4. Sector ladder (clickable) ──────────────────────────────────
  {
    const g = svg.append('g');
    const selected = view.sector || null;
    const pos = sectors.filter((s) => s.avg > 0), neg = sectors.filter((s) => s.avg <= 0);
    const step = 28.6, x = 80, w = 135;
    const tag = (sec: SectorStat, ty: number) => {
      const upSec = sec.avg > 0, isSel = sec.name === selected, level = y(sec.avg);
      g.append('path').attr('d', `M${x + w},${ty + 11} C${x + w + 20},${ty + 11} ${x + w + 10},${level} ${x + w + 35},${level} L${L.plotX1},${level}`)
        .attr('fill', 'none').attr('stroke', C.blue)
        .attr('stroke-width', isSel ? 1.6 : 0.9).attr('opacity', isSel ? 0.8 : selected ? 0.1 : 0.28);
      const choose = () => ui.selectSector(isSel ? null : sec.name);
      const tg = g.append('g').attr('class', 'sector-tag' + (isSel ? ' selected' : ''))
        .attr('role', 'button').attr('tabindex', 0).attr('aria-pressed', isSel)
        .attr('aria-label', `${sec.name} sector, ${fmtPct(sec.avg)}. ${isSel ? 'Show all sectors' : 'Show only this sector'}`)
        .on('click', choose)
        .on('keydown', (ev: KeyboardEvent) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); choose(); } });
      tg.append('title').text(isSel ? 'Click to show all sectors' : `Show only ${sec.name} stocks (${sec.adv}/${sec.n} rising)`);
      tg.append('rect').attr('class', 'sector-box').attr('x', x).attr('y', ty).attr('width', w).attr('height', 22)
        .attr('fill', isSel ? (upSec ? C.gl : C.rl) : C.panel)
        .attr('stroke', isSel ? C.ink : C.grid).attr('stroke-width', isSel ? 1.5 : 1).attr('rx', 1.5);
      const body = tg.append('g').attr('opacity', selected && !isSel ? 0.45 : 1);
      body.append('text').text(sec.name).attr('x', x + 6).attr('y', ty + 10).attr('class', 'sector-name');
      body.append('text').text(fmtPct(sec.avg)).attr('x', x + w - 5).attr('y', ty + 10).attr('text-anchor', 'end')
        .attr('class', 'sector-pct').attr('fill', upSec ? C.g : C.r);
      const segs = 16, sw = (w - 12) / segs, green = Math.round((sec.adv / sec.n) * segs);
      for (let i = 0; i < segs; i++) {
        body.append('rect').attr('x', x + 6 + i * sw).attr('y', ty + 14).attr('width', sw - 1).attr('height', 4)
          .attr('fill', i < green ? C.g : C.rs).attr('opacity', i < green ? 0.85 : 0.9);
      }
    };
    pos.forEach((s, i) => tag(s, L.yTop + 4 + i * step));
    neg.forEach((s, i) => tag(s, L.yBot - 26 - (neg.length - 1 - i) * step));
    badge(g, 2, 90, L.yTop - 6);
  }

  // ── 5. Strength column + brackets ─────────────────────────────────
  {
    const g = svg.append('g');
    const x0 = L.plotX1, x1 = L.colX1;
    const bracket = (list: Analysed[], col: string, fill: string) => {
      if (!list.length) return;
      const ys = list.map((s) => y(s.pct));
      const a = Math.min(...ys) - 5, b = Math.max(...ys) + 5;
      g.append('rect').attr('x', x0).attr('y', a).attr('width', x1 - x0).attr('height', b - a).attr('fill', fill).attr('opacity', 0.55);
      g.append('path').attr('d', `M${x0 + 7},${a} H${x0} V${b} H${x0 + 7}`).attr('fill', 'none').attr('stroke', col).attr('stroke-width', 2);
      g.append('path').attr('d', `M${x1 - 7},${a} H${x1} V${b} H${x1 - 7}`).attr('fill', 'none').attr('stroke', col).attr('stroke-width', 2);
    };
    bracket(snap.topBulls, C.gd, C.gl);
    bracket(snap.topBears, C.rd, C.rl);
    g.append('line').attr('x1', x0).attr('x2', x0).attr('y1', L.yTop).attr('y2', L.yBot).attr('stroke', C.blueSoft).attr('opacity', 0.7);
    g.append('line').attr('x1', x1).attr('x2', x1).attr('y1', L.yTop).attr('y2', L.yBot).attr('stroke', C.blueSoft).attr('opacity', 0.4);
    snap.stocks.forEach((s) => {
      g.append('rect').attr('class', 'score-bar').attr('data-sym', s.symbol)
        .attr('x', x0 + 2).attr('y', y(s.pct) - 1.1)
        .attr('width', Math.max(3, ((x1 - x0 - 8) * s.score) / 100)).attr('height', 2.2)
        .attr('fill', s.bull ? C.g : C.r).attr('opacity', top.has(s.symbol) ? 0.9 : 0.35);
    });
    g.append('line').attr('x1', L.plotX0).attr('x2', x1).attr('y1', y0).attr('y2', y0).attr('stroke', C.navy).attr('stroke-width', 1.6);
    const pill = (x: number, text: string, anchor: 'start' | 'end') => {
      const w = text.length * 7 + 8, rx = anchor === 'end' ? x - w : x;
      g.append('rect').attr('x', rx).attr('y', y0 - 19).attr('width', w).attr('height', 14).attr('fill', C.panel).attr('stroke', C.line);
      g.append('text').text(text).attr('x', rx + w / 2).attr('y', y0 - 9).attr('text-anchor', 'middle').attr('class', 'pill-label');
    };
    pill(x0 - 4, String(snap.stocks.length), 'end');
    pill(x1 + 4, String(snap.topBulls.length + snap.topBears.length), 'start');
    g.append('circle').attr('cx', x0).attr('cy', y0).attr('r', 4).attr('fill', C.navy);
    g.append('circle').attr('cx', x1).attr('cy', y0).attr('r', 4).attr('fill', C.blueMid);
    badge(g, 5, (L.plotX1 + L.colX1) / 2, L.yTop - 6);
  }

  // ── 6. Divider + leaderboards ─────────────────────────────────────
  {
    const g = svg.append('g');
    const mid = (L.bullY1 + L.bearY0) / 2;
    g.append('path').attr('d', `M${L.colX1},${y0} C${L.colX1 + 70},${y0} ${L.cardX - 70},${mid} ${L.cardX},${mid}`)
      .attr('fill', 'none').attr('stroke', C.blue).attr('stroke-width', 1.6);
    g.append('line').attr('x1', L.cardX).attr('x2', W).attr('y1', mid).attr('y2', mid).attr('stroke', C.blue).attr('stroke-width', 1.2).attr('opacity', 0.6);
    g.append('circle').attr('cx', L.cardX).attr('cy', mid).attr('r', 5).attr('fill', C.navy);
  }
  drawCards(svg, market, snap, y, ui, fmtPx);
}

interface Slot { x: number; y: number; w: number; h: number; col: number }

function slotsFor(Y0: number, Y1: number): Slot[] {
  const cols: Slot[][] = [];
  let x = L.cardX;
  COLS.forEach((c, ci) => {
    const gap = 6, h = (Y1 - Y0 - (c.n - 1) * gap) / c.n;
    cols.push(Array.from({ length: c.n }, (_, k) => ({ x, y: Y0 + k * (h + gap), w: c.w, h, col: ci })));
    x += c.w + 8;
  });
  return [...cols[3], ...cols[2], ...cols[1], ...cols[0]]; // rank 1 = biggest card
}

function drawCards(svg: Sel, market: Market, snap: Snapshot, y: YScale, ui: BoardUi, fmtPx: (v: number) => string) {
  const bullSlots = slotsFor(L.bullY0, L.bullY1), bearSlots = slotsFor(L.bearY0, L.bearY1);
  for (const [list, slots, col] of [[snap.topBulls, bullSlots, C.g], [snap.topBears, bearSlots, C.r]] as const) {
    const g = svg.append('g');
    list.forEach((s, i) => {
      const sl = slots[i], ys = y(s.pct), ye = sl.y + sl.h / 2, xe = sl.x;
      g.append('path').attr('class', 'flow').attr('data-sym', s.symbol)
        .attr('d', `M${L.colX1},${ys} C${L.colX1 + 60},${ys} ${xe - 60},${ye} ${xe},${ye}`)
        .attr('fill', 'none').attr('stroke', col).attr('stroke-width', 0.8).attr('opacity', 0.45);
    });
  }
  const g = svg.append('g');
  for (const [Y0, Y1, col] of [[L.bullY0, L.bullY1, C.gs], [L.bearY0, L.bearY1, C.rs]] as const) {
    g.append('rect').attr('x', L.cardX - 4).attr('y', Y0 - 4).attr('width', W - L.cardX - 6).attr('height', Y1 - Y0 + 8)
      .attr('fill', 'none').attr('stroke', col).attr('stroke-width', 1);
  }
  let idx = 0;
  snap.topBulls.forEach((s, i) => drawCard(g, market, s, i + 1, bullSlots[i], true, ui, idx++, fmtPx));
  snap.topBears.forEach((s, i) => drawCard(g, market, s, i + 1, bearSlots[i], false, ui, idx++, fmtPx));
  for (const [list, slots] of [[snap.topBulls, bullSlots], [snap.topBears, bearSlots]] as const) {
    for (let i = list.length; i < 20; i++) {
      g.append('rect').attr('x', slots[i].x).attr('y', slots[i].y).attr('width', slots[i].w).attr('height', slots[i].h)
        .attr('fill', 'none').attr('stroke', C.grid).attr('stroke-dasharray', '3 3');
    }
  }
  badge(g, 6, L.cardX - 14, 14);
}

function drawCard(g: Sel, market: Market, s: Analysed, rank: number, sl: Slot, bull: boolean, ui: BoardUi, cardIdx: number, fmtPx: (v: number) => string) {
  const { x, y, w, h, col } = sl;
  const main = bull ? C.g : C.r, dark = bull ? C.gd : C.rd;
  const cg = g.append('g').attr('class', 'card').attr('data-sym', s.symbol)
    .attr('role', 'link').attr('tabindex', 0)
    .attr('aria-label', `${s.symbol}, rank ${rank} ${bull ? 'bullish' : 'bearish'}, ${fmtPct(s.pct)}. Open stock page`);
  const clipId = 'clip-' + cardIdx;
  cg.append('clipPath').attr('id', clipId).append('rect').attr('x', x).attr('y', y).attr('width', w).attr('height', h);
  cg.append('rect').attr('x', x).attr('y', y).attr('width', w).attr('height', h).attr('fill', C.panel);
  cg.append('rect').attr('x', x).attr('y', y).attr('width', w).attr('height', h).attr('fill', bull ? 'url(#cardBull)' : 'url(#cardBear)');

  const inner = cg.append('g').attr('clip-path', `url(#${clipId})`);
  const fs = [11.5, 13.5, 15.5, 19][col];
  const hdr = col === 3 ? 62 : col === 0 ? 30 : fs * 2 + 12;

  // Volume profile, order blocks, session candle, last price
  const ax0 = x + 4, ax1 = x + w - 6, ay0 = y + hdr, ay1 = y + h - 4;
  if (ay1 - ay0 > 10 && s.high > s.low) {
    const { buy, sell } = s.profile;
    const bins = buy.length, rowH = (ay1 - ay0) / bins;
    const maxV = Math.max(...buy.map((b, k) => b + sell[k])) || 1;
    const py = d3.scaleLinear([s.low, s.high], [ay1, ay0]);
    const room = ax1 - ax0 - 14;
    for (let k = 0; k < bins; k++) {
      const tot = buy[k] + sell[k];
      if (!tot) continue;
      const yk = ay1 - (k + 1) * rowH, len = (room * tot) / maxV, bw = (len * buy[k]) / tot;
      inner.append('rect').attr('x', ax0).attr('y', yk + 0.35).attr('width', bw).attr('height', Math.max(0.6, rowH - 0.7))
        .attr('fill', C.g).attr('opacity', bull ? 0.75 : 0.45);
      inner.append('rect').attr('x', ax0 + bw).attr('y', yk + 0.35).attr('width', Math.max(0, len - bw)).attr('height', Math.max(0.6, rowH - 0.7))
        .attr('fill', C.r).attr('opacity', bull ? 0.45 : 0.75);
    }
    s.obs.slice(-3).forEach((ob) => { // the latest three; older ones only add clutter
      const oc = ob.type === 'bull' ? C.gd : C.rd;
      const ya = clamp(py(ob.top), ay0, ay1), yb = clamp(py(ob.bottom), ay0, ay1);
      if (yb - ya < 0.5) return;
      inner.append('rect').attr('x', ax0).attr('y', ya).attr('width', ax1 - ax0).attr('height', yb - ya)
        .attr('fill', ob.mitigated ? `url(#${ob.type === 'bull' ? 'hatchG' : 'hatchR'})` : oc)
        .attr('fill-opacity', ob.mitigated ? 1 : 0.28);
      inner.append('path').attr('d', `M${ax1 - 4},${ya} H${ax1} V${yb} H${ax1 - 4}`).attr('fill', 'none').attr('stroke', oc).attr('stroke-width', 1.2);
    });
    const cx = ax0 + (ax1 - ax0) * 0.62;
    inner.append('line').attr('x1', cx).attr('x2', cx).attr('y1', py(s.high)).attr('y2', py(s.low)).attr('stroke', C.ink).attr('opacity', 0.45);
    const ya = py(Math.max(s.open, s.ltp)), yb = py(Math.min(s.open, s.ltp));
    inner.append('rect').attr('x', cx - 1.5).attr('y', ya).attr('width', 3).attr('height', Math.max(1, yb - ya)).attr('fill', dark);
    const yp = py(s.ltp);
    inner.append('line').attr('x1', ax0).attr('x2', ax1).attr('y1', yp).attr('y2', yp)
      .attr('stroke', C.ink).attr('stroke-dasharray', '3 2').attr('stroke-width', 0.8).attr('opacity', 0.7);
    if (col >= 2) {
      const txt = fmtPx(s.ltp), bw = txt.length * 5.8 + 8, by = clamp(yp - 7, ay0, ay1 - 13);
      inner.append('rect').attr('x', ax1 - bw).attr('y', by).attr('width', bw).attr('height', 13).attr('fill', C.tagBg);
      inner.append('text').text(txt).attr('x', ax1 - bw / 2).attr('y', by + 9.5).attr('text-anchor', 'middle').attr('class', 'price-tag');
    }
  }

  // Header (drawn over the profile so the symbol always reads)
  let tx = x + 6, ty = y + fs + 4;
  if (col === 3) {
    cg.append('rect').attr('x', x + 8).attr('y', y + 9).attr('width', 26).attr('height', 26).attr('rx', 3).attr('fill', dark);
    cg.append('text').text(s.symbol.replace(/[^A-Z]/g, '').slice(0, 2)).attr('x', x + 21).attr('y', y + 26).attr('text-anchor', 'middle')
      .attr('fill', C.tagFg).attr('font-size', 11).attr('font-weight', 700).attr('font-family', FONT_NUM);
    tx = x + 42; ty = y + 25;
  }
  cg.append('text').text(s.symbol).attr('x', tx).attr('y', ty).attr('class', 'card-sym').attr('font-size', fs)
    .attr('paint-order', 'stroke').attr('stroke', C.panel).attr('stroke-width', 3);
  cg.append('text').text(fmtPct(s.pct)).attr('x', tx).attr('y', ty + fs * 0.95).attr('class', 'card-pct')
    .attr('font-size', fs * 0.8).attr('fill', dark).attr('paint-order', 'stroke').attr('stroke', C.panel).attr('stroke-width', 3);
  if (col === 3) {
    const filled = Math.round(s.score / 20);
    for (let i = 0; i < 5; i++) cg.append('circle').attr('cx', x + 12 + i * 7).attr('cy', y + 48).attr('r', 2.3).attr('fill', i < filled ? main : C.grid);
  }

  // Sector agreement: filled "S" = moving with its sector, hollow = moving alone.
  const sg = cg.append('g').attr('transform', `translate(${x + w - 31},${y + 11.5})`);
  sg.append('circle').attr('r', 5.5).attr('fill', s.withSector ? main : C.panel).attr('stroke', s.withSector ? main : C.mute2).attr('stroke-width', 1);
  sg.append('text').text('S').attr('text-anchor', 'middle').attr('dy', 3).attr('class', 'sector-mark').attr('fill', s.withSector ? '#fff' : C.mute2);
  sg.append('title').text(s.withSector ? `Moving with its sector (${s.sector} ${fmtPct(s.sectorAvg)})` : `Moving alone: ${s.sector} is ${fmtPct(s.sectorAvg)}`);
  const sw = liveSweep(s, ui.t());
  if (sw) {
    const tg = cg.append('g').attr('transform', `translate(${x + w - 26},${y + 21})`);
    tg.append('rect').attr('width', 22).attr('height', 11).attr('rx', 2).attr('fill', C.amberSoft).attr('stroke', C.amber);
    tg.append('text').text(sw.type === 'bear' ? 'SW↓' : 'SW↑').attr('x', 11).attr('y', 8.3).attr('text-anchor', 'middle').attr('class', 'sweep-tag');
    tg.append('title').text(`Liquidity sweep: ${sw.type === 'bear' ? 'ran above' : 'dropped below'} ${sw.name} at ${market.timeLabel(sw.idx)} and closed back inside`);
  }
  if (ui.isShortlisted(s.symbol)) {
    cg.append('text').text('★').attr('x', x + w - 39).attr('y', y + 15.5).attr('text-anchor', 'end').attr('class', 'star-mark')
      .append('title').text('In your shortlist');
  }
  cg.append('rect').attr('x', x + w - 22).attr('y', y + 5).attr('width', 18).attr('height', 13).attr('fill', C.panel).attr('stroke', main);
  cg.append('text').text(String(rank).padStart(2, '0')).attr('x', x + w - 13).attr('y', y + 14.5).attr('text-anchor', 'middle').attr('class', 'rank');
  cg.append('rect').attr('class', 'card-border').attr('x', x).attr('y', y).attr('width', w).attr('height', h)
    .attr('fill', 'transparent').attr('stroke', bull ? C.gs : C.rs).attr('stroke-width', col === 3 ? 1.6 : 1);

  cg.on('mouseenter', (ev: MouseEvent) => { ui.highlight(s.symbol, true); ui.tooltip(ev, s, rank); })
    .on('mousemove', (ev: MouseEvent) => ui.tooltip(ev, s, rank))
    .on('mouseleave', () => { ui.highlight(s.symbol, false); ui.tooltip(null); })
    .on('click', (ev: MouseEvent) => { ui.tooltip(null); ui.openStock(s.symbol, ev); })
    .on('auxclick', (ev: MouseEvent) => { if (ev.button === 1) ui.openStock(s.symbol, ev); })
    .on('keydown', (ev: KeyboardEvent) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ui.openStock(s.symbol, ev); } });
}
